import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons';
import './OfficeRoster.scss';

import { OfficeContext } from './Office';
import { getOfficeRoster, getUserOfficeSchedule } from '../../api/office';
import { getSmartImage } from '../../helpers/helpers';
import { formatName, formatTimeClean } from '../../helpers/office_helpers';

const OfficeRoster = () => {
  const ctx = useContext(OfficeContext);
  const {
    selectedDate,
    selectedDayLabel,
    focusedUserId,
    setHighlightedUserId,
    lockedUserId,
    setLockedUserId,
    setFocusedUserFloorIds,
    setFocusedUserSchedule,
    bookingVersion
  } = ctx;
  const [roster, setRoster] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const scheduleCacheRef = useRef({});
  const searchInputRef = useRef(null);
  const lockedUserInfoRef = useRef(null);

  useEffect(() => {
    fetchRoster();
  }, [selectedDate, bookingVersion]);
  useEffect(() => {
    scheduleCacheRef.current = {};
    if (lockedUserId) fetchSchedule(lockedUserId);
  }, [selectedDate, bookingVersion]);

  const fetchRoster = async () => {
    try {
      const resp = await getOfficeRoster(selectedDate);
      setRoster(resp?.users || []);
    } catch (e) {
      console.error('Failed to load roster', e);
    }
  };

  const fetchSchedule = useCallback(
    async userId => {
      const cacheKey = `${userId}-${selectedDate}`;
      if (scheduleCacheRef.current[cacheKey]) {
        const cached = scheduleCacheRef.current[cacheKey];
        setScheduleData(cached);
        setFocusedUserSchedule(cached);
        const floorIds = [...new Set(cached.map(b => b.space?.OfficeFloor_id).filter(Boolean))];
        setFocusedUserFloorIds(floorIds);
        return;
      }
      const starts_at = `${selectedDate}T07:00:00`;
      const ends_at = `${selectedDate}T21:00:00`;
      try {
        const resp = await getUserOfficeSchedule(userId, starts_at, ends_at);
        const bookings = resp?.bookings || [];
        scheduleCacheRef.current[cacheKey] = bookings;
        setScheduleData(bookings);
        setFocusedUserSchedule(bookings);
        const floorIds = [...new Set(bookings.map(b => b.space?.OfficeFloor_id).filter(Boolean))];
        setFocusedUserFloorIds(floorIds);
      } catch {
        setScheduleData([]);
        setFocusedUserSchedule([]);
        setFocusedUserFloorIds([]);
      }
    },
    [selectedDate, setFocusedUserFloorIds, setFocusedUserSchedule]
  );

  const handleMouseEnter = useCallback(
    user => {
      setHoveredUserId(user.id);
      setHighlightedUserId(user.id);
      fetchSchedule(user.id);
    },
    [setHighlightedUserId, fetchSchedule]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredUserId(null);
    setScheduleData(null);
    if (!lockedUserId) {
      setHighlightedUserId(null);
      setFocusedUserFloorIds([]);
      setFocusedUserSchedule([]);
    }
  }, [lockedUserId, setHighlightedUserId, setFocusedUserFloorIds, setFocusedUserSchedule]);

  const handleClick = useCallback(
    user => {
      if (lockedUserId === user.id) {
        setLockedUserId(null);
        lockedUserInfoRef.current = null;
        setHighlightedUserId(null);
        setFocusedUserFloorIds([]);
        setFocusedUserSchedule([]);
      } else {
        setLockedUserId(user.id);
        lockedUserInfoRef.current = { id: user.id, name: user.name, image: user.image };
        fetchSchedule(user.id);
      }
    },
    [lockedUserId, setLockedUserId, setHighlightedUserId, fetchSchedule]
  );

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const filtered = searchQuery ? roster.filter(u => u.name?.toLowerCase().includes(searchQuery.toLowerCase())) : roster;

  const activeUserId = focusedUserId || hoveredUserId;
  const lockedUser = lockedUserId ? lockedUserInfoRef.current || roster.find(u => u.id === lockedUserId) : null;

  const clearLock = () => {
    setLockedUserId(null);
    lockedUserInfoRef.current = null;
    setHighlightedUserId(null);
    setFocusedUserFloorIds([]);
    setFocusedUserSchedule([]);
  };

  return (
    <div className='office-roster'>
      <div className='roster-header'>
        <span className='roster-label'>In {selectedDayLabel.toLowerCase()}</span>
        {lockedUser && (
          <div className='roster-locked-pill'>
            <span className='locked-pill-name'>{formatName(lockedUser.name)}</span>
            <button className='locked-pill-close' onClick={clearLock}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
        <div className='roster-search-input'>
          <input
            ref={searchInputRef}
            type='text'
            placeholder='Search by name'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onBlur={() => {
              if (!searchQuery) closeSearch();
            }}
          />
        </div>
      </div>

      <div className={cn('roster-strip', { dimming: !!activeUserId })}>
        {filtered.length === 0 && <div className='roster-empty-card' />}
        {filtered.slice(0, 45).map(user => {
          const img = getSmartImage(user.image);
          const isActive = activeUserId === user.id;
          const isLocked = lockedUserId === user.id;
          return (
            <div
              key={user.id}
              className={cn('roster-card', { active: isActive, locked: isLocked, dimmed: activeUserId && !isActive })}
              onMouseEnter={() => handleMouseEnter(user)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(user)}
            >
              <div className='card-avatar-wrap'>
                {img ? (
                  <img src={img} alt={user.name} className='card-avatar' />
                ) : (
                  <div className='card-avatar placeholder'>{user.name?.charAt(0) || '?'}</div>
                )}
              </div>
              <span className='card-name'>{formatName(user.name)}</span>

              {isActive && hoveredUserId === user.id && scheduleData && (
                <div className='roster-schedule-dropdown' onClick={e => e.stopPropagation()}>
                  {scheduleData.length === 0 ? (
                    <div className='schedule-empty'>No bookings</div>
                  ) : (
                    scheduleData.map(b => (
                      <div key={b.id} className='schedule-entry'>
                        <span className='entry-space'>{b.space?.name || 'Space'}</span>
                        <span className='entry-time'>
                          {formatTimeClean(b.starts_at)} – {formatTimeClean(b.ends_at)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length > 45 && (
          <div>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px', width: '10px' }} />
            {filtered.length - 45} more
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeRoster;
